/**
 * Authentication Models and DTOs
 *
 * This file contains all authentication-related data transfer objects,
 * interfaces, and type definitions for the GBC application.
 */

/**
 * Login request DTO - Data sent to the authentication API
 */
export interface LoginRequest {
  /** User's email address */
  email: string
  /** User's password */
  password: string
}
export interface AuthSsoUserRole {
  _id: string
  code: string
  name: string
  roleClesame: string
}

export interface AuthSsoUserRoleClesame {
  _id: string | null
  code: string
  name: string
}

export interface AuthSsoUserGroup {
  _id: string
  code: string
  name: string
}

export interface AuthSsoUser {
  id: string
  code: string
  firstName: string
  lastName: string
  name: string
  mail: string
  profession: string | null
  role: AuthSsoUserRole
  roleClesame: AuthSsoUserRoleClesame
  groups: AuthSsoUserGroup[]
  civility: string
}
/**
 * SSO Authentication response from the API.
 * Shared by both the initial SSO callback and the refresh endpoint.
 */
export interface AuthSsoResponse {
  /** JWT access token */
  token: string
  /** SSO refresh token (EGG2) */
  refresh: string
  /** Refresh token expiration date (ISO string) */
  refreshExpires: string
  user: AuthSsoUser
}

/**
 * Authentication response from the API
 */
export interface AuthResponse {
  /** JWT access token */
  token: string
  /** Refresh token for obtaining new access tokens */
  refreshToken: string
  /** Expiration date of the refresh token */
  refreshTokenExpiresAt: string
  /** User's role in the system */
  role: UserRole
  /** Unique user identifier */
  userId: number
}

/**
 * Available user roles in the system
 */
export type UserRole = 'notaire' | 'admin' | 'user' | 'guest'

/**
 * User information extracted from JWT token
 */
export interface UserInfo {
  /** Unique user identifier */
  userId: string
  /** User's role */
  role: UserRole
  /** Token expiration timestamp */
  exp: number
  /** Token issued at timestamp */
  iat: number
  /** Token issuer */
  iss: string
  /** Token audience */
  aud: string
  /** JWT ID */
  jti: string
}

/**
 * Authentication state interface
 */
export interface AuthState {
  /** Whether user is currently authenticated */
  isAuthenticated: boolean
  /** Current user information */
  user: UserInfo | null
  /** Access token */
  token: string | null
  /** Refresh token */
  refreshToken: string | null
  /** Refresh token expiration date */
  refreshTokenExpiresAt: Date | null
  /** Whether authentication is in progress */
  isLoading: boolean
  /** Last authentication error */
  error: string | null
}

/**
 * Token storage interface for persistence
 */
export interface TokenStorage {
  token: string
  refreshToken: string
  refreshTokenExpiresAt: string
  userId: string | number
  role: UserRole
  userInfo?: string
}

/**
 * Authentication error types
 */
export enum AuthErrorType {
  INVALID_CREDENTIALS = 'INVALID_CREDENTIALS',
  NETWORK_ERROR = 'NETWORK_ERROR',
  TOKEN_EXPIRED = 'TOKEN_EXPIRED',
  REFRESH_FAILED = 'REFRESH_FAILED',
  UNAUTHORIZED = 'UNAUTHORIZED',
  SERVER_ERROR = 'SERVER_ERROR',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
}

/**
 * Authentication error interface
 */
export interface AuthError {
  type: AuthErrorType
  message: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  details?: any
}

/**
 * Remember me option for login
 */
export interface RememberMeOptions {
  /** Whether to remember the user */
  remember: boolean
  /** Duration in days to remember the user */
  duration?: number
}
